import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteTransactionById } from 'api/transactions';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { isQueryKeyIndexMeta } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteTransactionDialogProps extends AlertDialog.AlertDialogRootProps {}
const DeleteTransactionDialog = (props: DeleteTransactionDialogProps) => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const { children, ...rest } = props;
    const { data: transaction } = useCurrentTransactionOrById();
    const transactionId = transaction?.id!;
    const tickets = transaction?.tickets ?? [];
    const { accountId } = useCurrentAccount();

    const { mutate: deleteTransaction } = useMutation({
        mutationFn: () => deleteTransactionById(transactionId),
        onSuccess: () => {
            toast.success('Deleted transaction successfully');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
                predicate: isQueryKeyIndexMeta,
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.appeals(accountId),
            });
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? 'Failed to delete transaction.');
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                        <AlertDialog.Description>
                            {tickets.length > 0 &&
                                'Tickets are associated with this transaction and will be deleted automatically. '}
                            You will not be able to undo this action.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => deleteTransaction()}>
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteTransactionDialogTrigger = AlertDialog.Trigger;

const Root = DeleteTransactionDialog;
const Trigger = DeleteTransactionDialogTrigger;

export {
    DeleteTransactionDialog,
    DeleteTransactionDialogTrigger,
    //
    Root,
    Trigger,
};
